import FilterListIcon from '@mui/icons-material/FilterList';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import {styled} from '@mui/material/styles';
import {useMutation} from '@tanstack/react-query';
import React, {useState} from 'react';
import {useHistory} from 'react-router-dom';

import {ConfigConstant} from '../../constants/config';
import {pxToRem} from '../../utils';
import {convertObjectToURLParams, convertURLParamsToObject} from '../../utils/helper';
import CustomButton from '../CustomButton/CustomButton';
import InfoTooltip from '../InfoTooltip/InfoTooltip';

import FilterDrawer from './FilterDrawer';
const StyledWrapperDiv = styled('div')(({theme}) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginBottom: pxToRem(16),
}));

const StyledWrapperChipDiv = styled('div')(({theme}) => ({
  justifyContent: 'flex-start',
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  width: '100%',
}));

const StyledChip = styled(Chip)(({theme, filterid}) => ({
  margin: theme.spacing(0.5),
  backgroundColor: ConfigConstant.FILTER_IDS_DIFF_COLOR.includes(filterid)
    ? theme.palette.tertiary.light2
    : theme.palette.secondary.light2,
  boxShadow: ConfigConstant.FILTER_IDS_DIFF_COLOR.includes(filterid)
    ? theme.palette.boxShadow.chip2
    : theme.palette.boxShadow.chip,
  fontWeight: 'bold',
  '&:hover': {
    backgroundColor: ConfigConstant.FILTER_IDS_DIFF_COLOR.includes(filterid)
      ? theme.palette.tertiary.light
      : theme.palette.secondary.light,
  },
}));

const StyledBox = styled(Box)(({theme}) => ({
  display: 'flex',
  width: '25%',
  alignItems: 'center',
  justifyContent: 'flex-end',
}));

const StyledCustomButtonApply = styled(CustomButton)(({theme}) => ({
  width: pxToRem(100),
}));

const StyledCustomButtonFilter = styled(CustomButton)(({theme}) => ({
  width: pxToRem(100),
}));

const FilterBar = ({
  dataQuery,
  filters,
  setFilters,
  showFilterActionButton,
  showApplyActionButton,
  changeColumnVisibility,
  allowDateRangeSelection,
  setPagination,
  setSnackBarAlert,
  shouldValidateMultiOpsSelection,
}) => {
  const history = useHistory();

  const [openfilterDrawer, setOpenFilterDrawer] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(filters[1]);

  const handleFilterDelete = (filter) => {
    let isLastFilter = true;
    setFilters((prevState) => {
      return prevState.map((ele) => {
        if (ele.id === filter?.id) {
          return {
            ...ele,
            isApplied: false,
            value: null,
            displayValue: null,
          };
        } else {
          if (ele.isApplied === true) {
            isLastFilter = false;
            setSelectedFilter(ele);
          }
          return ele;
        }
      });
    });
    isLastFilter && setSelectedFilter(filters[1]);
  };

  const onFilterClick = (filter) => {
    setSelectedFilter(filter);
    setOpenFilterDrawer(!openfilterDrawer);
  };

  const IsMultiOpsSelected = () => {
    const opsFilter = filters?.find((filter) => filter.id === 'Operation');
    return (
      opsFilter?.value?.length > 1 ||
      opsFilter?.value?.includes(ConfigConstant.MONGO_CONFIG?.OPERATION_TYPES?.PRODUCT_TOGGLE?.code)
    );
  };

  const validateMultiOpsSelection = () => {
    if (!shouldValidateMultiOpsSelection) return true;
    const isMultiOpsSelected = IsMultiOpsSelected();
    const vstFilter = filters.find((filter) => filter.id === 'Virtual Store Code');
    if (isMultiOpsSelected) {
      if (vstFilter?.value?.length > 0) {
        return true;
      } else {
        setSnackBarAlert('Please add atleast one virtual store code filter.');
        return false;
      }
    }
    return true;
  };

  const setQueryParams = () => {
    const urlParams = convertURLParamsToObject(window.location.search);
    const appliedFilters = {};
    if (urlParams?.search) {
      appliedFilters.search = urlParams.search;
    }
    filters?.forEach((filter) => {
      if (filter?.isApplied) {
        appliedFilters[filter?.key] = filter.value;
      }
    });
    const queryParams = convertObjectToURLParams(appliedFilters);
    history.push({search: queryParams});
  };

  const onApplyFilterClick = () => {
    if (showApplyActionButton) {
      if (!validateMultiOpsSelection()) {
        return;
      }
      changeColumnVisibility && changeColumnVisibility();
      setPagination && setPagination((prevState) => ({...prevState, pageIndex: 0}));
      dataQuery.forEach((query) => {
        query.refetch();
      });
    }
    setOpenFilterDrawer(false);
    setQueryParams();
  };

  const onClearFilterClickMutation = useMutation(
    async () => {
      const isMultiOpsSelected = IsMultiOpsSelected();
      filters?.forEach((filter) => {
        if (
          filter?.isDeleteable &&
          !(shouldValidateMultiOpsSelection && isMultiOpsSelected && filter?.id === 'Virtual Store Code')
        ) {
          handleFilterDelete(filter);
        }
      });
    },
    {
      onSuccess: async () => {
        dataQuery.forEach((query) => {
          query.refetch();
        });
      },
    }
  );

  const onClearFilterClick = () => {
    if (showApplyActionButton) {
      onClearFilterClickMutation.mutate();
    }
    history.push({pathname: history?.location?.pathname});
    setOpenFilterDrawer(false);
  };

  const information = 'Refresh data by clicking Apply after changing filters';

  return (
    <>
      <StyledWrapperDiv>
        <StyledWrapperChipDiv>
          {filters?.map((filter, index) => {
            if (filter?.isApplied) {
              let label = '';
              if (filter?.selectType === ConfigConstant.FILTER_SELECT_TYPE.MULTIPLE) {
                if (Array.isArray(filter?.displayValue) && filter.displayValue.length > 1) {
                  label = `${filter.displayValue.length} selections`;
                } else {
                  label = filter?.displayValue[0];
                }
              } else {
                label = Array.isArray(filter?.displayValue) ? filter?.displayValue[0] : filter?.displayValue;
              }
              return filter?.isDeleteable ? (
                <StyledChip
                  key={index}
                  label={`${filter?.id}: ${label}`}
                  onDelete={() => handleFilterDelete(filter)}
                  onClick={() => onFilterClick(filter)}
                  filterid={filter?.id}
                />
              ) : (
                <StyledChip
                  key={index}
                  label={`${filter?.id}: ${label}`}
                  onClick={() => onFilterClick(filter)}
                  filterid={filter?.id}
                />
              );
            }
            return null;
          })}
        </StyledWrapperChipDiv>
        <StyledBox>
          {showApplyActionButton && (
            <Box sx={{mr: 1, display: 'flex', alignItems: 'center'}}>
              <InfoTooltip information={information} />
              <StyledCustomButtonApply
                text={'Apply'}
                className={'color-secondary'}
                onClick={onApplyFilterClick}
                isLoading={dataQuery[0]?.isLoading || dataQuery[0]?.isFetching}
              />
            </Box>
          )}
          {showFilterActionButton && (
            <>
              <StyledCustomButtonFilter
                text={
                  <>
                    <FilterListIcon /> Filters
                  </>
                }
                className={'color-secondary'}
                onClick={() => setOpenFilterDrawer(!openfilterDrawer)}
              />
              <FilterDrawer
                open={openfilterDrawer}
                onToggleOff={setOpenFilterDrawer}
                filters={filters}
                setFilters={setFilters}
                selectedFilter={selectedFilter}
                showApplyActionButton={showApplyActionButton}
                setSelectedFilter={setSelectedFilter}
                onApplyFilter={onApplyFilterClick}
                onClearFilterClick={onClearFilterClick}
                allowDateRangeSelection={allowDateRangeSelection}
                isLoading={dataQuery?.[0]?.isLoading || dataQuery?.[0]?.isFetching}
              />
            </>
          )}
        </StyledBox>
      </StyledWrapperDiv>
    </>
  );
};

export default FilterBar;
